import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface AccountingPeriodDto {
  accountingPeriodId: string;
  nameAr: string;
  nameEn: string | null;
  startDate: string;
  endDate: string;
  isClosed: boolean;
  closedAt: string | null;
}

export interface AccountingPeriodInput {
  nameAr: string;
  nameEn?: string | null;
  startDate: string;
  endDate: string;
}

const QK = ['accounting-periods'] as const;

export function useAccountingPeriods() {
  return useQuery({
    queryKey: QK,
    queryFn: async () =>
      (await apiClient.get<AccountingPeriodDto[]>('/api/accounting/periods')).data,
  });
}

export function useCreatePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: AccountingPeriodInput) =>
      (await apiClient.post<AccountingPeriodDto>('/api/accounting/periods', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdatePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, req }: { id: string; req: AccountingPeriodInput }) =>
      (await apiClient.put<AccountingPeriodDto>(`/api/accounting/periods/${id}`, req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useSetPeriodClosed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, closed }: { id: string; closed: boolean }) =>
      (await apiClient.post<AccountingPeriodDto>(
        `/api/accounting/periods/${id}/${closed ? 'close' : 'reopen'}`,
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export interface PeriodClosingResult {
  posted: boolean;
  entryNumber: string | null;
  netIncome: number;
}

export function usePostClosingVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post<PeriodClosingResult>(`/api/accounting/periods/${id}/closing-voucher`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeletePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/accounting/periods/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
